import type { FieldValues, Message, Path, UseControllerProps } from 'react-hook-form';
import { OptionWithHint } from '#components/patterns/forms/select/option/OptionWithHint.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import type { Network } from '#dn-types/network.ts';
import { SelectAsyncControlled } from '../../forms/select/select-async/SelectAsyncControlled.tsx';
import { makeAllNetworksQueryOptions } from './SelectNetwork.queries.ts';

type Props<T extends FieldValues, N extends Path<T>> = {
  errorMessage?: Message;
  label: React.ReactNode;
  /** For UIs where we do not want to show the CIDR hint, set this to true */
  hideCIDR?: boolean;
  className?: string;
  filterNetworks?: (network: Network) => boolean;
} & UseControllerProps<T, N> &
  Omit<React.ComponentProps<typeof SelectAsyncControlled>, 'getItemsQueryOptions' | 'control' | 'renderOptions'>;

export function SelectNetwork<T extends FieldValues, N extends Path<T>>({
  label,
  errorMessage,
  filterNetworks,
  control,
  name,
  className,
  ...props
}: Props<T, N>) {
  return (
    <SelectAsyncControlled
      className={className}
      getItemsQueryOptions={makeAllNetworksQueryOptions(filterNetworks)}
      control={control}
      name={name}
      label={label}
      placeholder="Please select…"
      errorMessage={errorMessage}
      renderOptions={({ onChange, items }) => (
        <>
          {items.map((itm) => (
            <SelectItem key={itm.value} value={itm.value} onClick={() => onChange(itm.value)} searchable={false}>
              {itm.hint && !props.hideCIDR ?
                <OptionWithHint value={itm.label} hint={itm.hint} />
              : itm.label}
            </SelectItem>
          ))}
        </>
      )}
      {...props}
    />
  );
}
